import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Brain, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  RefreshCw, Target, Clock, Users, Lightbulb, BarChart3,
  ChevronRight, Loader2, Sparkles
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PIPPredictions() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [selectedPIP, setSelectedPIP] = useState(null);
  const [predictionDetail, setPredictionDetail] = useState(null);
  
  const fetchData = async () => {
    try {
      const [batchRes, insightsRes] = await Promise.all([
        fetch(`${API_URL}/api/pip/predictions/batch`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/pip/predictions/insights`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (batchRes.ok) setBatchData(await batchRes.json());
      if (insightsRes.ok) setInsights(await insightsRes.json());
    } catch (error) {
      toast.error('Failed to load prediction data');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [token]);
  
  const analyzePIP = async (pipId) => {
    setAnalyzing(pipId);
    try {
      const response = await fetch(`${API_URL}/api/pip/predictions/analyze/${pipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setPredictionDetail(result);
        setSelectedPIP(pipId);
        toast.success('Analysis complete');
        fetchData(); // Refresh batch data
      } else {
        toast.error('Analysis failed');
      }
    } catch (error) {
      toast.error('Error analyzing PIP');
    } finally {
      setAnalyzing(null);
    }
  };
  
  const getProbabilityColor = (prob) => {
    if (prob >= 75) return 'text-emerald-600';
    if (prob >= 50) return 'text-amber-600';
    return 'text-red-600';
  };
  
  const getProbabilityBg = (prob) => {
    if (prob >= 75) return 'bg-emerald-500';
    if (prob >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };
  
  const getImpactBadge = (impact) => {
    const colors = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-amber-100 text-amber-700 border-amber-200',
      low: 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return colors[impact] || colors.low;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="pip-predictions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent flex items-center gap-2">
            <Brain className="h-7 w-7 text-teal-500" />
            PIP Success Predictions
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            AI-powered analysis of Performance Improvement Project success probability
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                <Target className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Active PIPs</p>
                <p className="text-xl font-bold">{batchData?.total_active_pips || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Sparkles className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Success Rate</p>
                <p className={`text-xl font-bold ${getProbabilityColor(batchData?.average_success_probability || 0)}`}>
                  {batchData?.average_success_probability || 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">High Risk</p>
                <p className="text-xl font-bold text-red-600">{insights?.high_risk_pips || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Low Risk</p>
                <p className="text-xl font-bold text-emerald-600">{insights?.low_risk_pips || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* PIP List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Active PIPs Analysis</CardTitle>
              <CardDescription>Click "Analyze" to generate AI predictions</CardDescription>
            </CardHeader>
            <CardContent>
              {batchData?.predictions?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No active PIPs found</p>
                  <p className="text-sm">Create PIPs to start predicting success</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {batchData?.predictions?.map((pip) => (
                    <div 
                      key={pip.pip_id}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedPIP === pip.pip_id 
                          ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' 
                          : 'hover:border-gray-300'
                      }`}
                      onClick={() => pip.has_prediction && setSelectedPIP(pip.pip_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{pip.pip_title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{pip.category}</Badge>
                            <Badge variant="secondary" className="text-xs">{pip.status}</Badge>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {pip.has_prediction ? (
                            <div className="text-right">
                              <p className={`text-lg font-bold ${getProbabilityColor(pip.success_probability)}`}>
                                {pip.success_probability}%
                              </p>
                              <p className="text-xs text-muted-foreground">{pip.confidence_level} confidence</p>
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              onClick={(e) => { e.stopPropagation(); analyzePIP(pip.pip_id); }}
                              disabled={analyzing === pip.pip_id}
                            >
                              {analyzing === pip.pip_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Brain className="h-4 w-4" />
                              )}
                              <span className="ml-2">Analyze</span>
                            </Button>
                          )}
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                      
                      {pip.has_prediction && (
                        <div className="mt-3">
                          <Progress 
                            value={pip.success_probability} 
                            className={`h-2 ${getProbabilityBg(pip.success_probability)}`}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Insights Panel */}
        <div className="space-y-4">
          {/* Common Risk Factors */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Common Risk Factors
              </CardTitle>
            </CardHeader>
            <CardContent>
              {insights?.common_risk_factors?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No risk data yet</p>
              ) : (
                <div className="space-y-2">
                  {insights?.common_risk_factors?.map((rf, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="truncate flex-1">{rf.factor}</span>
                      <Badge variant="outline" className="ml-2">{rf.frequency}x</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Success Factors */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                Success Factors
              </CardTitle>
            </CardHeader>
            <CardContent>
              {insights?.common_success_factors?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No success data yet</p>
              ) : (
                <div className="space-y-2">
                  {insights?.common_success_factors?.map((sf, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="truncate flex-1">{sf.factor}</span>
                      <Badge variant="outline" className="ml-2 bg-emerald-50 text-emerald-700">{sf.frequency}x</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* AI Insights */}
          <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/10 dark:to-indigo-900/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-purple-500" />
                AI Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {insights?.insights?.map((insight, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5">•</span>
                    <span>{insight}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Prediction Detail Modal/Panel */}
      {predictionDetail && selectedPIP === predictionDetail.pip_id && (
        <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/10 dark:to-cyan-900/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-teal-500" />
                Detailed Analysis: {predictionDetail.pip_title}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setSelectedPIP(null); setPredictionDetail(null); }}>
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Success Probability */}
            <div className="flex items-center gap-6">
              <div className={`w-24 h-24 rounded-full border-4 ${getProbabilityBg(predictionDetail.prediction?.success_probability)} border-opacity-30 flex items-center justify-center`}>
                <span className={`text-3xl font-bold ${getProbabilityColor(predictionDetail.prediction?.success_probability)}`}>
                  {predictionDetail.prediction?.success_probability}%
                </span>
              </div>
              <div>
                <p className="text-lg font-semibold">Success Probability</p>
                <p className="text-muted-foreground">{predictionDetail.prediction?.summary}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Risk Factors */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Risk Factors
                </h4>
                <div className="space-y-2">
                  {predictionDetail.prediction?.risk_factors?.map((rf, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{rf.factor}</span>
                        <Badge className={getImpactBadge(rf.impact)}>{rf.impact}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">Mitigation: {rf.mitigation}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Success Factors */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  Success Factors
                </h4>
                <div className="space-y-2">
                  {predictionDetail.prediction?.success_factors?.map((sf, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{sf.factor}</span>
                        <Badge className="bg-emerald-100 text-emerald-700">{sf.impact}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Milestones */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-500" />
                Predicted Milestones
              </h4>
              <div className="flex gap-4 overflow-x-auto pb-2">
                {predictionDetail.prediction?.milestone_predictions?.map((m, idx) => (
                  <div key={idx} className="flex-shrink-0 w-40 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200">
                    <p className="text-xs text-muted-foreground">Week {m.week}</p>
                    <p className="text-lg font-bold text-blue-600">{m.expected_progress}%</p>
                    <p className="text-xs mt-1">{m.milestone}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Recommendations */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-purple-500" />
                Recommendations
              </h4>
              <ul className="space-y-2">
                {predictionDetail.prediction?.recommendations?.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="text-purple-500 font-bold">{idx + 1}.</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
